import type { FilterType } from "@repo/shared/rift";
import {
  boardFiltersToRift,
  isBoardFilterComplete,
} from "@repo/shared/utils/board-filters-to-rift";
import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  subDays,
} from "date-fns";

import { formatRelativeIntervalPhrase } from "~/pages/board/board-filters/constants";
import { boardFilters$ } from "./board-filters.crud";
import type { BoardFilter } from "./board-filters.types";

export { isBoardFilterComplete };

/** Returns board filters for a board sorted by order (lexicographic). */
export const getSortedBoardFilters = (boardId: string): BoardFilter[] => {
  const all = boardFilters$.get() ?? {};
  const forBoard = Object.values(all).filter(
    (f): f is NonNullable<typeof f> => f != null && f.boardId === boardId,
  );
  forBoard.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
  return forBoard;
};

/** Whether the board has at least one complete date-range filter (RELATIVE or BETWEEN). */
export const hasDateBoardFilter = (boardId: string): boolean => {
  return getSortedBoardFilters(boardId).some(
    (f) =>
      isBoardFilterComplete(f) &&
      (f.mode === "RELATIVE" || f.mode === "BETWEEN"),
  );
};

/** Maps a fixed rolling-window day count to a human-readable previous-period label. */
const relativeDaysToPeriodLabel = (days: number): string => {
  switch (days) {
    case 1:
      return "Previous day";
    case 7:
      return "Previous week";
    case 30:
      return "Previous month";
    case 180:
      return "Previous 6 months";
    case 365:
      return "Previous year";
    default:
      return `Previous ${days} days`;
  }
};

/** Formats a previous-period date range, including the year only when it differs from today. */
const formatPreviousRange = (lower: Date, upper: Date): string => {
  const currentYear = new Date().getFullYear();
  const includeYear =
    lower.getFullYear() !== currentYear || upper.getFullYear() !== currentYear;
  const pattern = includeYear ? "MMM d, yyyy" : "MMM d";
  return `${format(lower, pattern)} \u2013 ${format(upper, pattern)}`;
};

/**
 * Returns a human-readable label describing the previous comparison period for a
 * board's first complete date-range filter (RELATIVE or BETWEEN), or `null` when
 * the board has no such filter.
 *
 * - RELATIVE rolling windows map to "Previous week/month/..." labels.
 * - BETWEEN ranges resolve to the concrete previous window `[start - duration, start)`,
 *   formatted as an inclusive date range (e.g. "Mar 3 - Mar 31").
 */
export const getPreviousPeriodLabel = (boardId: string): string | null => {
  const filter = getSortedBoardFilters(boardId).find(
    (f) =>
      isBoardFilterComplete(f) &&
      (f.mode === "RELATIVE" || f.mode === "BETWEEN"),
  );
  if (!filter) return null;

  if (filter.mode === "RELATIVE") {
    const relative = filter.relative;
    if (!relative) return null;
    if (relative.interval === "DAY") {
      const days = Math.max(1, Math.floor(relative.value));
      return relativeDaysToPeriodLabel(days);
    }
    return `Previous ${formatRelativeIntervalPhrase(relative)}`;
  }

  const range = filter.value as { start?: string; end?: string } | null;
  if (!range?.start || !range?.end) return null;
  const start = parseISO(range.start);
  const end = parseISO(range.end);
  if (!isValid(start) || !isValid(end)) return null;

  const durationDays = differenceInCalendarDays(end, start);
  const prevLower = subDays(start, durationDays);
  const prevUpperInclusive = subDays(start, 1);
  return formatPreviousRange(prevLower, prevUpperInclusive);
};

/**
 * Returns board filters for a board as FilterType[], scoped to a specific dataset.
 * Only columns belonging to the given dataset are included; filters with no
 * matching columns are skipped. Filters without a complete value are excluded.
 */
export const getBoardFiltersAsFilterTypes = (
  boardId: string,
  datasetId: string,
): FilterType[] => {
  return boardFiltersToRift(getSortedBoardFilters(boardId), datasetId);
};
